import React, { useState } from 'react';
import { getProfileVideos, getAIInsights } from '../services/api';
import { cacheAnalysisResult, CACHE_TYPES } from '../services/cache';
import { hasReachedLimit, incrementSearchCount } from '../services/searchLimit';
import TikTokEmbed from './TikTokEmbed';
import SearchLimitBanner from './SearchLimitBanner';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Card,
  Grid,
  Avatar,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
} from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ShareIcon from '@mui/icons-material/Share';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PeopleIcon from '@mui/icons-material/People';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const ConversionRateAnalysis = () => {
  const [username, setUsername] = useState('');
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username) {
      setError('Please enter a username');
      return;
    }

    if (hasReachedLimit()) {
      setError("You've reached the free search limit. Join the waitlist for unlimited access!");
      return;
    }

    // Clear previous data when starting a new search
    setVideos([]);
    setError('');
    setInsights(null);

    setLoading(true);
    try {
      const data = await getProfileVideos(username, true);
      const analysisData = {
        videos: data.videos,
        username,
        timestamp: new Date().toISOString()
      };
      
      // Cache the results
      cacheAnalysisResult(CACHE_TYPES.CONVERSION, '', analysisData);
      setVideos(data.videos);
      incrementSearchCount();

      // Get AI insights for conversion
      setLoadingInsights(true);
      try {
        const insightsData = await getAIInsights(data.videos, 'conversion');
        if (insightsData?.insights) {
          setInsights(insightsData.insights);
        }
      } catch (insightError) {
        console.error('Error getting insights:', insightError);
        // Don't fail the whole component if insights fail
      } finally {
        setLoadingInsights(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch profile videos');
      setVideos([]);
    } finally {
      setLoading(false);
    }
  };

  // Calculate conversion rate as (comments + shares) / views * 100
  const calculateConversion = (video) => {
    if (!video?.stats) return 0;
    const interactions = (video.stats.commentCount || 0) + (video.stats.shareCount || 0);
    const views = video.stats.playCount || 0;
    return views > 0 ? (interactions / views) * 100 : 0;
  };

  const averageConversion = videos.reduce((acc, video) => 
    acc + calculateConversion(video), 0) / (videos.length || 1);

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%)',
          borderRadius: 2,
          p: 3
        }}>
          <Avatar sx={{ 
            width: 56, 
            height: 56,
            background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
          }}>
            <PeopleIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Conversion Rate Analysis
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Track how effectively your content converts viewers to followers
            </Typography>
          </Box>
        </Box>

        <SearchLimitBanner />

        {/* Search Form */}
        <Card sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
          borderRadius: 3,
        }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Box sx={{ position: 'relative', mb: 3 }}>
              <TextField
                fullWidth
                label="TikTok Username"
                placeholder="Enter username without @"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'white',
                    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
                    '&:hover': {
                      boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.1)',
                    },
                    '&.Mui-focused': {
                      boxShadow: '0px 4px 25px rgba(0, 0, 0, 0.1)',
                    }
                  }
                }}
              />
              <Tooltip
                title="Enter a TikTok username to analyze their content's conversion rates"
                placement="top"
                arrow
              >
                <HelpOutlineIcon 
                  sx={{ 
                    position: 'absolute',
                    right: { xs: 8, sm: 12 },
                    top: '50%',
                    transform: 'translateY(-50%)',
                    opacity: 0.6,
                    cursor: 'help'
                  }} 
                />
              </Tooltip>
            </Box>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || hasReachedLimit()}
              fullWidth
              sx={{
                height: 48,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #7C3AED 0%, #DB2777 100%)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PeopleIcon />
                <span>{loading ? 'Analyzing...' : 'Analyze Profile'}</span>
              </Box>
            </Button>
          </Box>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && !error && videos.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              Enter a TikTok username above to analyze their conversion rates
            </Typography>
          </Box>
        )}

        {videos.length > 0 && (
          <>
            {/* Overall Stats */}
            <Paper sx={{ 
              p: 3, 
              mb: 4,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <PeopleIcon sx={{ 
                  fontSize: 24,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  AI-Powered Conversion Analysis
                </Typography>
              </Box>
              {/* AI Insights */}
              <Paper sx={{ 
                p: 3, 
                mb: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                color: 'white',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <SmartToyIcon sx={{ fontSize: 24, color: 'white' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'white' }}>
                    AI Content Analysis
                  </Typography>
                </Box>
                {loadingInsights ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress sx={{ color: 'white' }} />
                  </Box>
                ) : insights ? (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-line', color: 'rgba(255, 255, 255, 0.9)' }}>
                    {insights}
                  </Typography>
                ) : (
                  <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                    No AI insights available
                  </Typography>
                )}
              </Paper>

              {/* Conversion Stats */}
              <Card sx={{ 
                p: 3, 
                mb: 4,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                color: 'white',
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: { xs: 1, sm: 2 },
                  mb: 1
                }}>
                  <TrendingUpIcon sx={{ 
                    fontSize: { xs: 18, sm: 20 },
                    color: 'white'
                  }} />
                  <Typography variant="h6" sx={{ color: 'white', fontSize: { xs: '1rem', sm: '1.1rem' }, lineHeight: 1.2 }}>
                    Average Conversion Rate
                  </Typography>
                </Box>
                <Typography variant="h4" sx={{ color: 'white', mb: 2 }}>
                  {averageConversion.toFixed(2)}%
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                  Of viewers engage through comments and shares
                </Typography>
              </Card>
            </Paper>

            {/* Recent Videos */}
            <Paper sx={{ 
              p: 3,
              borderRadius: 3,
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <PeopleIcon sx={{ 
                  fontSize: 24,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }} />
                <Typography variant="h6" sx={{ 
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Recent Videos Performance
                </Typography>
              </Box>
              <Grid container spacing={3}>
                {videos.map((video) => (
                  <Grid item xs={12} sm={6} lg={4} xl={4} key={video.id}>
                    <Card sx={{ 
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      minWidth: 0,
                      width: '100%'
                    }}>
                      <Box sx={{ 
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                        flex: 1
                      }}>
                        <Box sx={{ width: '100%', overflow: 'hidden' }}>
                          <TikTokEmbed url={video.videoUrl} />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {video.description}
                        </Typography>

                        {/* Metrics with new flexible layout */}
                        <Box sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 2,
                          justifyContent: 'space-between',
                          mb: 3
                        }}>
                          {/* Likes */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <FavoriteIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                color: '#ef4444',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {(video.stats?.diggCount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Likes
                            </Typography>
                          </Box>

                          {/* Views */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <VisibilityIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {(video.stats?.playCount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Views
                            </Typography>
                          </Box>

                          {/* Shares */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <ShareIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {(video.stats?.shareCount || 0).toLocaleString()}
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Shares
                            </Typography>
                          </Box>

                          {/* Conversion */}
                          <Box sx={{ 
                            flex: '1 1 45%',
                            display: 'flex',
                            flexDirection: 'column',
                            p: 2,
                            borderRadius: 2,
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              gap: { xs: 1, sm: 2 },
                              mb: 1,
                              overflow: 'hidden'
                            }}>
                              <TrendingUpIcon sx={{ 
                                fontSize: { xs: 18, sm: 20 },
                                background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                                flexShrink: 0
                              }} />
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '1rem', sm: '1.1rem' }, 
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {calculateConversion(video).toFixed(1)}%
                              </Typography>
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                              Conversion
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ 
                          p: 3, 
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #8B5CF6 0%, #EC4899 100%)',
                          color: 'white',
                          mt: 'auto'
                        }}>
                          <Typography variant="subtitle2">
                            Performance: {calculateConversion(video) > averageConversion ? 'Above' : 'Below'} Average
                          </Typography>
                        </Box>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </>
        )}
      </Box>
    </Container>
  );
};

export default ConversionRateAnalysis;
